import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsHeatmap from 'highcharts/modules/heatmap';
import HighchartsBoost from 'highcharts/modules/boost';
import HighchartsReact from 'highcharts-react-official';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { SolutionSummary } from '../../services/amsoApi';

type HcModuleFactory = (hc: typeof Highcharts) => void;

if (typeof HighchartsHeatmap === 'function') {
  (HighchartsHeatmap as unknown as HcModuleFactory)(Highcharts);
}
if (typeof HighchartsBoost === 'function') {
  (HighchartsBoost as unknown as HcModuleFactory)(Highcharts);
}

interface ParetoChartProps {
  solutions: SolutionSummary[];
  selectedIndex: number;
  primaryObjective: string;
  onSelectSolution: (index: number) => void;
}

const fmtAxis = (val: number): string => {
  if (Math.abs(val) >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (Math.abs(val) >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
  return String(Math.round(val));
};

const interpolateColor = (t: number): string => {
  const r = Math.round(227 + (13 - 227) * t);
  const g = Math.round(242 + (71 - 242) * t);
  const b = Math.round(253 + (161 - 253) * t);
  return `rgb(${r},${g},${b})`;
};

export default function ParetoChart({
  solutions,
  selectedIndex,
  primaryObjective,
  onSelectSolution,
}: ParetoChartProps) {
  const isRiskPrimary = primaryObjective === 'risk';

  const options = useMemo<Highcharts.Options>(() => {
    const primaryLabel = isRiskPrimary ? 'Risk Score' : 'Condition Improvement';
    const secondaryLabel = isRiskPrimary
      ? 'Condition Improvement'
      : 'Risk Score';

    const rawColors: number[] = solutions.map(s => {
      const metrics = (s.metrics ?? {}) as Record<string, any>;
      const condVsBaseline: number =
        metrics.condition_vs_baseline ?? s.condition_improvement ?? 0;
      return isRiskPrimary ? condVsBaseline : s.risk ?? 0;
    });
    const colorMin = rawColors.length > 0 ? Math.min(...rawColors) : 0;
    const colorMax = rawColors.length > 0 ? Math.max(...rawColors) : 1;
    const colorRange = colorMax - colorMin || 1;

    const points: Highcharts.PointOptionsObject[] = solutions.map((s, i) => {
      const metrics = (s.metrics ?? {}) as Record<string, any>;
      const condVsBaseline: number =
        metrics.condition_vs_baseline ?? s.condition_improvement ?? 0;
      const x = isRiskPrimary ? s.risk ?? 0 : condVsBaseline;
      const y = (s.budget_utilization ?? 0) * 100;
      const colorVal = rawColors[i];
      const t = colorMin === colorMax ? 1 : (colorVal - colorMin) / colorRange;
      const pointColor = interpolateColor(t);
      const isSelected = s.solution_index === selectedIndex;

      return {
        x,
        y,
        color: pointColor,
        custom: { solutionIndex: s.solution_index, colorVal },
        marker: isSelected
          ? {
              radius: 11,
              lineWidth: 3,
              lineColor: '#1a1a1a',
              symbol: 'circle',
              states: { hover: { radiusPlus: 2 } },
            }
          : {
              radius: 5,
              symbol: 'circle',
              states: { hover: { radiusPlus: 3 } },
            },
      };
    });

    return {
      chart: {
        type: 'scatter',
        height: 420,
        animation: { duration: 300 },
        zooming: {
          type: 'xy',
          resetButton: {
            theme: {
              fill: '#fff',
              stroke: '#ccc',
              r: 4,
              style: { fontSize: '11px' },
            },
          },
        },
        style: { fontFamily: '"Inter", "Roboto", sans-serif' },
        spacing: [20, 20, 15, 15],
      },
      title: { text: undefined },
      xAxis: {
        title: {
          text: primaryLabel,
          style: { fontSize: '12px', fontWeight: '600', color: '#555' },
        },
        labels: {
          formatter() {
            return fmtAxis(this.value as number);
          },
          style: { fontSize: '11px' },
        },
        gridLineWidth: 1,
        gridLineDashStyle: 'Dot',
        gridLineColor: '#e8e8e8',
        lineColor: '#ccc',
        tickColor: '#ccc',
      },
      yAxis: {
        title: {
          text: 'Budget Utilization (%)',
          style: { fontSize: '12px', fontWeight: '600', color: '#555' },
        },
        labels: { format: '{value}%', style: { fontSize: '11px' } },
        min: 0,
        max: 100,
        gridLineColor: '#e8e8e8',
        gridLineDashStyle: 'Dot',
      },
      colorAxis:
        colorMin === colorMax
          ? { visible: false }
          : {
              min: colorMin,
              max: colorMax,
              minColor: '#e3f2fd',
              maxColor: '#0d47a1',
              labels: {
                formatter() {
                  return fmtAxis(this.value as number);
                },
                style: { fontSize: '10px' },
              },
              marker: { color: '#0d47a1' },
            },
      legend: {
        enabled: colorMin !== colorMax,
        title: {
          text: secondaryLabel,
          style: { fontSize: '11px', fontWeight: '500', color: '#666' },
        },
        align: 'right',
        verticalAlign: 'middle',
        layout: 'vertical',
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        borderColor: '#ddd',
        borderRadius: 8,
        shadow: { color: 'rgba(0,0,0,0.08)', offsetX: 0, offsetY: 2, width: 8 },
        style: { fontSize: '12px', lineHeight: '18px' },
        headerFormat: '',
        pointFormat:
          '<b style="font-size:13px">Solution {point.custom.solutionIndex}</b><br/><br/>' +
          `${primaryLabel}: <b>{point.x:,.0f}</b><br/>` +
          'Budget Utilization: <b>{point.y:.1f}%</b><br/>' +
          `${secondaryLabel}: <b>{point.custom.colorVal:,.0f}</b>`,
      },
      plotOptions: {
        scatter: {
          cursor: 'pointer',
          point: {
            events: {
              click() {
                const idx = (this.options as any).custom?.solutionIndex;
                if (idx != null) onSelectSolution(idx);
              },
            },
          },
        },
      },
      series: [
        {
          type: 'scatter',
          name: 'Solutions',
          data: points,
          states: { inactive: { opacity: 0.6 } },
        },
      ],
      credits: { enabled: false },
    };
  }, [
    solutions,
    selectedIndex,
    primaryObjective,
    onSelectSolution,
    isRiskPrimary,
  ]);

  if (solutions.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            mb: 1,
          }}>
          <Typography variant="h6">Solutions Front</Typography>
          {solutions.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              Drag to zoom &middot; Click a point to select
            </Typography>
          )}
        </Box>
        {solutions.length === 1 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Recommended Plan produces a single solution.
          </Typography>
        )}
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          updateArgs={[true, true, true]}
        />
      </CardContent>
    </Card>
  );
}
